/**
 * @fileoverview IANA zone offset arithmetic built on `Intl.DateTimeFormat`,
 * so it reads no ambient timezone and gives the same answer in a browser and
 * in Cloud Functions.
 */

import {getPermanentOffsetMinutes} from '../timeZone/permanentOffsets';
import {ONE_DAY_MS, ONE_MINUTE_MS} from '../utils/time';

/** One formatter per zone; constructing `Intl.DateTimeFormat` is slow. */
const zoneFormatters = new Map<string, Intl.DateTimeFormat>();

/** Zones `Intl` rejected; a failed construction costs as much as a good one. */
const invalidZones = new Set<string>();

function getZoneFormatter(timeZone: string): Intl.DateTimeFormat {
  const cached = zoneFormatters.get(timeZone);
  if (cached) {
    return cached;
  }
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    // h23 rather than `hour12: false`: the latter renders midnight as
    // hour "24" under some ICU versions, which Date.UTC then rolls
    // into the next day and reports as a 24-hour offset error.
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  zoneFormatters.set(timeZone, formatter);
  return formatter;
}

/**
 * Whether the runtime's `Intl` implementation recognises `timeZone` as an
 * IANA zone identifier.
 *
 * @param timeZone - Candidate zone identifier. Empty or absent is invalid.
 * @return `true` when `getZoneOffsetMinutes` can be called with it.
 * @throws Never.
 */
export function isValidTimeZone(timeZone: string | undefined): boolean {
  if (!timeZone || invalidZones.has(timeZone)) {
    return false;
  }
  try {
    getZoneFormatter(timeZone);
    return true;
  } catch (e) {
    if (e instanceof RangeError) {
      invalidZones.add(timeZone);
      return false;
    }
    throw e;
  }
}

/**
 * Minutes east of UTC observed by `timeZone` at a given instant.
 *
 * Derived by formatting the instant into the target zone's civil
 * fields and reading them back as if they were UTC; the difference is
 * the offset. This is the only supported way to get a *historical or
 * future* offset — `Date.prototype.getTimezoneOffset` reports the
 * runtime's own zone, and a fixed `UTC±H:MM` string cannot express a
 * zone whose offset changes.
 *
 * @param instantMs - Epoch milliseconds to sample the offset at. The
 *   offset of a zone is a function of the instant, not of the zone
 *   alone, so this argument is required.
 * @param timeZone - IANA zone identifier, e.g. `'Europe/Berlin'`.
 * @return Offset in minutes east of UTC, negative west of UTC.
 *   Rounded to the nearest minute. Zones whose rules older tzdata gets
 *   wrong take their offset from {@link getPermanentOffsetMinutes}.
 * @throws {RangeError} if `timeZone` is not a zone identifier the
 *   runtime's `Intl` implementation recognises.
 */
export function getZoneOffsetMinutes({
  instantMs,
  timeZone,
}: {
  instantMs: number;
  timeZone: string;
}): number {
  const permanentOffsetMinutes = getPermanentOffsetMinutes({
    timeZone,
    instantMs,
  });
  if (permanentOffsetMinutes !== undefined) {
    return permanentOffsetMinutes;
  }
  const parts = getZoneFormatter(timeZone).formatToParts(new Date(instantMs));

  const field = (type: Intl.DateTimeFormatPartTypes): number => {
    const found = parts.find(part => part.type === type);
    return found ? Number(found.value) : 0;
  };

  const asIfUtcMs = Date.UTC(
    field('year'),
    field('month') - 1,
    field('day'),
    field('hour'),
    field('minute'),
    field('second'),
  );

  // formatToParts has second resolution, so compare against the
  // instant truncated to the same resolution rather than letting a
  // sub-second remainder skew the division.
  const truncatedMs = Math.floor(instantMs / 1000) * 1000;
  return Math.round((asIfUtcMs - truncatedMs) / 60_000);
}

/**
 * Converts a wall-clock time in `timeZone` to the instant it names.
 *
 * The wall clock is passed as "wall-clock ms": the epoch ms that wall clock
 * would be if the zone were UTC (what `Date.UTC(y, m, d, h, min)` returns).
 * Two wall clocks have no single instant, and this resolves them the way
 * Temporal's `'compatible'` disambiguation does:
 *
 * - Ambiguous (clocks go back, the wall clock happens twice): the earlier
 *   instant, i.e. the one at the pre-transition offset.
 * - Nonexistent (clocks go forward, the wall clock is skipped): the
 *   instant at the pre-transition offset, which lands after the gap — the
 *   wall clock pushed forward by the gap's length.
 *
 * Assumes the zone changes offset at most once within a day of the wall
 * clock, which every IANA zone does.
 *
 * @param wallClockMs - The local wall clock, as if it were UTC.
 * @param timeZone - IANA zone identifier.
 * @return Epoch ms of the instant.
 * @throws {RangeError} if `timeZone` is not recognised.
 */
export function wallClockToInstantMs({
  wallClockMs,
  timeZone,
}: {
  wallClockMs: number;
  timeZone: string;
}): number {
  const offsetAt = (instantMs: number) =>
    getZoneOffsetMinutes({instantMs, timeZone}) * ONE_MINUTE_MS;
  const offsetBefore = offsetAt(wallClockMs - ONE_DAY_MS);
  const offsetAfter = offsetAt(wallClockMs + ONE_DAY_MS);
  const candidateBefore = wallClockMs - offsetBefore;
  if (offsetBefore === offsetAfter) {
    return candidateBefore;
  }
  const candidateAfter = wallClockMs - offsetAfter;
  const isBeforeValid = offsetAt(candidateBefore) === offsetBefore;
  const isAfterValid = offsetAt(candidateAfter) === offsetAfter;
  if (isBeforeValid && isAfterValid) {
    return Math.min(candidateBefore, candidateAfter);
  }
  if (isAfterValid) {
    return candidateAfter;
  }
  return candidateBefore;
}
